import { SkillCollectionViewer } from "@promptlayer/ui";
import { BoxesIcon } from "lucide-react";
import { useMemo } from "react";

import {
  getProviderSelectionLogoProvider,
  getProviderSelectionFromProvider,
  PROVIDER_SELECTION_LABELS,
  VENDOR_AGNOSTIC_PROVIDER,
} from "../skill-presets";
import { SkillCollectionWorkspaceSkeleton } from "../SkillCollectionWorkspaceSkeleton";
import { stripRootPathPrefix } from "../utils/path-utils";

import { ProviderWithLogo } from "@/components/ProviderWithLogo";
import { useGetSkillCollectionVersion } from "@/queries/skill-collections";

interface OverviewVersionBrowserProps {
  workspaceId: number;
  collectionId: string;
  rootPath: string;
  versionNumber: number | null;
  filePaths: string[];
  initialSelectedPath?: string | null;
  onSelectedPathChange?: (path: string | null) => void;
}

export const OverviewVersionBrowser = ({
  workspaceId,
  collectionId,
  rootPath,
  versionNumber,
  filePaths,
  initialSelectedPath,
  onSelectedPathChange,
}: OverviewVersionBrowserProps) => {
  const { data, isLoading } = useGetSkillCollectionVersion(
    workspaceId,
    collectionId,
    versionNumber,
  );
  const versionFilePaths = data?.version.file_paths ?? filePaths;
  const normalizedVersionFilePaths = useMemo(
    () =>
      versionFilePaths
        .map(path => {
          const isFolder = path.endsWith("/");
          const normalizedPath = isFolder ? path.slice(0, -1) : path;
          const relativePath = stripRootPathPrefix(normalizedPath, rootPath);

          if (!relativePath) {
            return null;
          }

          return isFolder ? `${relativePath}/` : relativePath;
        })
        .filter((path): path is string => Boolean(path)),
    [rootPath, versionFilePaths],
  );
  const providerSelection = getProviderSelectionFromProvider(data?.version.provider);

  if (isLoading) {
    return <SkillCollectionWorkspaceSkeleton />;
  }

  return (
    <SkillCollectionViewer
      className="h-full"
      filePaths={normalizedVersionFilePaths}
      files={data?.files ?? {}}
      initialSelectedPath={initialSelectedPath}
      onSelectedPathChange={onSelectedPathChange}
      openaiYamlContentVersionKey={versionNumber}
      licenseKey={import.meta.env.REACT_APP_AG_GRID_LICENSE_KEY}
      sidebarFooter={
        <div className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-xs shadow-sm">
          <div className="min-w-0 flex-1">
            {providerSelection === VENDOR_AGNOSTIC_PROVIDER ? (
              <div className="flex min-w-0 items-center text-gray-700">
                <div className="mr-1.5 flex h-3 w-3 shrink-0 items-center justify-center">
                  <BoxesIcon className="h-3 w-3 text-gray-500" />
                </div>
                <span className="truncate">{PROVIDER_SELECTION_LABELS[providerSelection]}</span>
              </div>
            ) : (
              <div className="flex min-w-0 items-center gap-2 text-gray-700">
                <ProviderWithLogo
                  provider={getProviderSelectionLogoProvider(providerSelection) ?? ""}
                  size="sm"
                  hideName
                  className="!mr-0"
                />
                <span className="truncate">{PROVIDER_SELECTION_LABELS[providerSelection]}</span>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
};
